import { gql, useMutation, useQuery } from "@apollo/client";
import cn from "classnames";
import React, { useCallback, useState } from "react";
import SettingsLayout from "../../../components/SettingsLayout";
import IconEdit from "../../../components/icon/IconEdit";
import If from "../../../components/utils/If";
import {
  EventType,
  LearningModule,
  LearningModuleInput,
} from "../../../schema";
import Badge from "../../../ui/Badge";
import DeleteIcon from "../../../ui/DeleteIcon";
import PrimaryButton from "../../../ui/PrimaryButton";
import Skeleton from "../../../ui/skeleton/Skeleton";
import { handleError, handleSuccess } from "../../../utils/Apollo";

interface GetLearningModulesQuery {
  learningModules: LearningModule[];
  eventTypes: EventType[];
}

export default function LearningModulesScene() {
  const { loading, data, error } = useQuery<GetLearningModulesQuery>(query, {
    errorPolicy: "all",
  });
  const [moduleName, setModuleName] = useState("");
  const [changedName, setChangedName] = useState("");
  const [editId, setEditId] = useState<string | null>(null);

  const [addModule] = useMutation(addMutation, {
    refetchQueries: [{ query }],
  });
  const [deleteModule] = useMutation(deleteMutation, {
    refetchQueries: [{ query }],
  });
  const [updateModule] = useMutation(updateMudaion, {
    refetchQueries: [{ query }],
  });
  const [toggleModuleStatus] = useMutation(toggleTypeMutation, {
    refetchQueries: [{ query }],
  });

  const onEventTypeChange = useCallback(
    (id: string) => {
      toggleModuleStatus({
        variables: { id },
      }).then(
        () => handleSuccess("Event type updated"),
        (apolloError) => handleError(apolloError)
      );
    },
    [toggleModuleStatus]
  );

  const onDelete = useCallback(
    (id: string) => {
      deleteModule({
        variables: { id },
      }).then(
        () => handleSuccess("Module deleted"),
        (apolloError) => handleError(apolloError)
      );
    },
    [deleteModule]
  );

  const onUpdate = useCallback(
    (id: string, value: LearningModuleInput) => {
      updateModule({
        variables: { id, data: value },
      }).then(
        () => handleSuccess("Module updated"),
        (apolloError) => handleError(apolloError)
      );
    },
    [updateModule]
  );

  const onFormSubmit = useCallback(
    (formData) => {
      addModule({
        variables: { data: formData },
      }).then(
        () => handleSuccess("Module added"),
        (apolloError) => handleError(apolloError)
      );
    },
    [addModule]
  );

  const header = () => (
    <h2 className="text-2xl text-gray-dark font-medium">Learning modules</h2>
  );
  const activeModules = data?.learningModules.filter((m) => m.active);
  const inactiveModules = data?.learningModules.filter((m) => !m.active);
  const modules = activeModules?.concat(inactiveModules || []);
  const selectedTypes = data?.eventTypes.filter((e) => e.withModules) || [];
  console.log(selectedTypes);
  const unselectedTypes = data?.eventTypes.filter((e) => !e.withModules) || [];

  return (
    <SettingsLayout title="Learning modules" header={header()}>
      <If condition={loading}>
        <Skeleton className="w-4/5 mb-4" />
        <Skeleton className="w-2/5" />
      </If>
      <If condition={!!data}>
        <p className="pb-6">
          By activating learning modules, your licensed trainers would need to
          set what modules they used for the event during the confirmation
          process. This will help you to track the used modules.
        </p>
        <div className="flex flex-col w-full gap-y-3">
          <h3 className="text-xl font-medium">Assigned types</h3>
          <p className="pb-2">
            Select event types for which you want to activate learning modules.
          </p>
          <div className="flex justify-between w-full gap-x-6 mb-2">
            <select
              onChange={(e) => onEventTypeChange(e.target?.value)}
              className="py-2 focus:border-blue-focus block w-full border-gray border rounded-md"
            >
              <option value="null">Select event type</option>
              {unselectedTypes
                .filter((e) => !e.withModules)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap w-full gap-x-4 gap-y-2 pb-6 pt-2">
          {selectedTypes.map((e) => (
            <Badge
              key={e.id}
              id={e.id}
              caption={e.name}
              type="blue"
              onClose={() => onEventTypeChange(e.id)}
            />
          ))}
        </div>
        <div className="flex flex-col w-full gap-y-3">
          <h3 className="text-xl font-medium">List of modules</h3>
          <div className="flex justify-between w-full gap-x-6 mb-2">
            <input
              type="text"
              onChange={(e) => setModuleName(e.target?.value)}
              value={moduleName}
              onKeyDown={(e) => {
                if (e.key === "Enter" && moduleName.trim().length > 0) {
                  onFormSubmit({ name: moduleName });
                  setModuleName("");
                }
              }}
              className="py-2 focus:border-blue-focus block w-full border-gray border rounded-md"
              placeholder="Module name"
            />
            <PrimaryButton
              className="w-32"
              disabled={moduleName.trim().length === 0}
              onClick={() => onFormSubmit({ name: moduleName.trim() })}
            >
              Add
            </PrimaryButton>
          </div>
          <div>
            {modules?.map((module) => (
              <div
                className={cn(
                  "group flex px-3 py-2.5 border-b border-gray-light justify-between gap-x-4 hover:bg-blue-table-hover",
                  { "text-gray-dark": !module.active }
                )}
                key={module.id}
              >
                {editId !== module.id && <span>{module.name}</span>}
                {editId === module.id && (
                  <input
                    type="text"
                    value={changedName}
                    onChange={(e) => setChangedName(e.target?.value)}
                    onKeyDown={(e) => {
                      const value = changedName.trim();
                      if (e.key === "Enter" && value.length > 0) {
                        onUpdate(module.id, { name: value });
                        setModuleName("");
                        setEditId(null);
                      }
                    }}
                    className="py-2 focus:border-blue-focus block w-full border-gray border rounded-md"
                  />
                )}
                <div className="gap-x-2 opacity-0 group-hover:opacity-80 flex-shrink-0">
                  <a
                    href="#"
                    onClick={() => {
                      setChangedName(module.name);
                      if (editId === module.id) {
                        setEditId(null);
                      } else {
                        setEditId(module.id);
                      }
                    }}
                  >
                    <IconEdit />
                  </a>
                  <DeleteIcon
                    title="Delete"
                    alwaysVisible={true}
                    onDelete={() => onDelete(module.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </If>
    </SettingsLayout>
  );
}

const query = gql`
  query GetLearningModulesQuery {
    learningModules: modules {
      id
      name
      active
    }
    eventTypes: eventTypes {
      id
      name
      withModules
    }
  }
`;

const addMutation = gql`
  mutation AddFormMutation($data: LearningModuleInput!) {
    createModule(data: $data) {
      id
      name
    }
  }
`;

const deleteMutation = gql`
  mutation DeleteFormMutation($id: ID!) {
    deleteModule(id: $id) {
      id
    }
  }
`;

const updateMudaion = gql`
  mutation UpdateFormMutation($id: ID!, $data: LearningModuleInput!) {
    updateModule(id: $id, data: $data) {
      id
      name
    }
  }
`;

const toggleTypeMutation = gql`
  mutation ToggleTypeMutation($id: ID!) {
    toggleModuleStatus(id: $id) {
      id
      withModules
    }
  }
`;
